import { Synth, SoundDef, AmbientLayer } from '../../engine/audio/Synth';
import { Vec3 } from '../../engine/math/vec3';
import { clamp } from '../../engine/math/easing';

/** Game sound library (§8.6). Every sound is a small synth recipe. */
export class Sfx {
  readonly synth: Synth;
  /** Camera position + right vector for positional audio. */
  readonly listener = new Vec3();
  readonly listenerRight = new Vec3(1, 0, 0);
  private lastStep = 0;

  constructor() { this.synth = new Synth(); }
  resume(): void { this.synth.resume(); }

  /** Pan/attenuate by world position. */
  private pos(def: SoundDef, at?: Vec3, maxDist = 40): void {
    if (!at) { this.synth.play(def); return; }
    const d = at.distanceTo(this.listener);
    if (d > maxDist) return;
    const g = 1 - clamp(d / maxDist, 0, 1);
    const dx = at.x - this.listener.x, dz = at.z - this.listener.z;
    const pan = clamp((dx * this.listenerRight.x + dz * this.listenerRight.z) / Math.max(1, d) * 0.8, -0.8, 0.8);
    this.synth.play(def, 0, g * g, pan);
  }

  pickup(): void {
    this.synth.play({ osc: 'triangle', freq: [520, 880], dur: 0.12, attack: 0.004, decay: 0.05, sustain: 0.5, release: 0.1, gain: 0.35 });
    this.synth.play({ osc: 'sine', freq: 1320, dur: 0.06, gain: 0.15 }, 0.08);
  }
  drop(at?: Vec3): void {
    this.pos({ osc: 'triangle', freq: [220, 120], dur: 0.1, gain: 0.3, filter: { type: 'lowpass', freq: 900 } }, at);
    this.pos({ osc: 'noise', freq: 0, dur: 0.05, gain: 0.12, filter: { type: 'lowpass', freq: 1200 } }, at);
  }
  swap(): void {
    this.drop();
    this.synth.play({ osc: 'triangle', freq: [440, 880], dur: 0.1, gain: 0.3 }, 0.06);
  }
  shake(): void {
    this.synth.play({ osc: 'square', freq: [180, 160], dur: 0.06, gain: 0.12, filter: { type: 'lowpass', freq: 800 } });
    this.synth.play({ osc: 'square', freq: [180, 160], dur: 0.06, gain: 0.12, filter: { type: 'lowpass', freq: 800 } }, 0.09);
  }
  step(surface: 'grass' | 'sand' | 'water' | 'stone' | 'wood' = 'grass', running = false): void {
    const t = this.synth.now;
    if (t - this.lastStep < 0.12) return;
    this.lastStep = t;
    const g = running ? 0.16 : 0.1;
    switch (surface) {
      case 'grass': this.synth.play({ osc: 'noise', freq: 0, dur: 0.04, gain: g, filter: { type: 'bandpass', freq: 1400, q: 0.7 }, release: 0.04 }); break;
      case 'sand': this.synth.play({ osc: 'noise', freq: 0, dur: 0.08, gain: g * 0.8, filter: { type: 'lowpass', freq: 2200 }, release: 0.06 }); break;
      case 'water': this.synth.play({ osc: 'noise', freq: 0, dur: 0.1, gain: g * 1.2, filter: { type: 'bandpass', freq: 2600, q: 1.5 }, release: 0.1 }); this.synth.play({ osc: 'sine', freq: [600, 300], dur: 0.06, gain: 0.06 }); break;
      case 'stone': this.synth.play({ osc: 'noise', freq: 0, dur: 0.03, gain: g * 1.1, filter: { type: 'highpass', freq: 1800 }, release: 0.05, reverb: 0.5 }); this.synth.play({ osc: 'triangle', freq: [380, 200], dur: 0.04, gain: 0.08, reverb: 0.5 }); break;
      case 'wood': this.synth.play({ osc: 'triangle', freq: [240, 150], dur: 0.06, gain: g * 1.4, filter: { type: 'lowpass', freq: 1200 } }); break;
    }
  }
  dodge(): void {
    this.synth.play({ osc: 'noise', freq: 0, dur: 0.18, gain: 0.18, filter: { type: 'bandpass', freq: [600, 2400], q: 1.2 }, attack: 0.02, release: 0.12 });
  }
  land(impact: number): void {
    this.synth.play({ osc: 'sine', freq: [140, 60], dur: 0.12, gain: 0.25 * impact + 0.05 });
  }
  hurt(): void {
    this.synth.play({ osc: 'square', freq: [300, 90], dur: 0.22, gain: 0.3, filter: { type: 'lowpass', freq: 1800 } });
    this.synth.play({ osc: 'noise', freq: 0, dur: 0.1, gain: 0.2, filter: { type: 'lowpass', freq: 900 } });
  }
  die(): void {
    this.synth.play({ osc: 'sawtooth', freq: [220, 40], dur: 0.9, gain: 0.3, filter: { type: 'lowpass', freq: [1800, 200] }, release: 0.3 });
  }
  respawn(): void {
    for (let i = 0; i < 3; i++) this.synth.play({ osc: 'triangle', freq: 440 * Math.pow(2, i * 5 / 12), dur: 0.2, gain: 0.2, reverb: 0.4 }, i * 0.12);
  }
  swing(): void {
    this.synth.play({ osc: 'noise', freq: 0, dur: 0.12, gain: 0.2, filter: { type: 'bandpass', freq: [800, 3000], q: 2 }, attack: 0.01, release: 0.08 });
  }
  hit(at?: Vec3): void {
    this.pos({ osc: 'square', freq: [200, 80], dur: 0.08, gain: 0.35, filter: { type: 'lowpass', freq: 1500 } }, at);
    this.pos({ osc: 'noise', freq: 0, dur: 0.06, gain: 0.25, filter: { type: 'highpass', freq: 2000 } }, at);
  }
  kill(at?: Vec3): void {
    this.pos({ osc: 'sawtooth', freq: [400, 60], dur: 0.35, gain: 0.3, filter: { type: 'lowpass', freq: [3000, 300] }, reverb: 0.4 }, at, 60);
    this.pos({ osc: 'noise', freq: 0, dur: 0.25, gain: 0.25, filter: { type: 'lowpass', freq: [4000, 400] }, reverb: 0.4 }, at, 60);
  }
  gate(at?: Vec3): void {
    // rattle then heavy chain rise
    for (let i = 0; i < 5; i++) this.pos({ osc: 'square', freq: 90 + i * 7, dur: 0.05, gain: 0.2, filter: { type: 'lowpass', freq: 700 } }, at, 60);
    this.synth.play({ osc: 'sawtooth', freq: [70, 110], dur: 1.5, gain: 0.25, filter: { type: 'lowpass', freq: 500 }, attack: 0.3, release: 0.4, reverb: 0.5, vibrato: { depth: 6, rate: 9 } }, 0.5);
    this.synth.play({ osc: 'triangle', freq: 220, dur: 0.4, gain: 0.25, reverb: 0.6 }, 2.0);
  }
  chime(): void {
    for (let i = 0; i < 4; i++) this.synth.play({ osc: 'sine', freq: [660, 1320, 990, 1760][i], dur: 0.5, gain: 0.18, reverb: 0.6, release: 0.4 }, i * 0.08);
  }
  heal(): void {
    this.synth.play({ osc: 'triangle', freq: [520, 1040], dur: 0.35, gain: 0.25, reverb: 0.5, release: 0.3 });
  }
  /** level 0..1 by proximity to metal; pitch rises as you get closer. */
  magnetHum(level: number): void {
    this.synth.voice('magnet', 'sawtooth', 70 + level * 190, level * 0.09, 500 + level * 900);
  }
  magnetPulse(): void {
    this.synth.play({ osc: 'sawtooth', freq: [80, 640], dur: 0.25, gain: 0.3, filter: { type: 'lowpass', freq: [400, 4000] }, release: 0.2 });
    this.synth.play({ osc: 'sine', freq: [1200, 300], dur: 0.3, gain: 0.15 }, 0.1);
  }
  plankPlace(at?: Vec3): void {
    this.pos({ osc: 'triangle', freq: [160, 90], dur: 0.15, gain: 0.45, filter: { type: 'lowpass', freq: 800 }, reverb: 0.3 }, at);
    this.pos({ osc: 'noise', freq: 0, dur: 0.08, gain: 0.2, filter: { type: 'lowpass', freq: 600 } }, at);
  }
  lantern(on: boolean): void {
    this.synth.play({ osc: 'triangle', freq: on ? [300, 600] : [600, 300], dur: 0.12, gain: 0.2 });
  }
  splash(at?: Vec3): void {
    this.pos({ osc: 'noise', freq: 0, dur: 0.3, gain: 0.3, filter: { type: 'bandpass', freq: [3000, 600], q: 1 }, release: 0.2 }, at);
  }
  sink(): void {
    this.synth.play({ osc: 'noise', freq: 0, dur: 1.2, gain: 0.3, filter: { type: 'lowpass', freq: [1200, 100] }, release: 0.5 });
    this.synth.play({ osc: 'sine', freq: [200, 40], dur: 1.2, gain: 0.25, release: 0.5 });
  }
  telegraph(kind: 'eel' | 'scarab' | 'stag' | 'wyrm', at?: Vec3, dur = 0.5): void {
    switch (kind) {
      case 'eel': this.pos({ osc: 'sawtooth', freq: [120, 400], dur, gain: 0.3, filter: { type: 'lowpass', freq: [600, 2500] }, attack: 0.05 }, at); break;
      case 'scarab': this.pos({ osc: 'square', freq: [180, 720], dur, gain: 0.2, filter: { type: 'bandpass', freq: [900, 3200], q: 2 }, vibrato: { depth: 30, rate: 28 } }, at); break;
      case 'stag': this.pos({ osc: 'noise', freq: 0, dur, gain: 0.3, filter: { type: 'bandpass', freq: [400, 1600], q: 1 } }, at); this.pos({ osc: 'sawtooth', freq: [60, 130], dur, gain: 0.25, filter: { type: 'lowpass', freq: 400 } }, at); break;
      case 'wyrm': this.pos({ osc: 'sine', freq: [900, 2400], dur, gain: 0.25, vibrato: { depth: 80, rate: 18 } }, at); break;
    }
  }
  wyrmHiss(at?: Vec3): void {
    this.pos({ osc: 'noise', freq: 0, dur: 0.4, gain: 0.3, filter: { type: 'highpass', freq: [800, 4000] }, release: 0.3 }, at);
  }
  stagStagger(at?: Vec3): void {
    this.pos({ osc: 'sawtooth', freq: [140, 70], dur: 0.3, gain: 0.35, filter: { type: 'lowpass', freq: 500 } }, at, 60);
    this.pos({ osc: 'noise', freq: 0, dur: 0.2, gain: 0.3, filter: { type: 'lowpass', freq: 800 } }, at, 60);
  }
  magpie(at?: Vec3): void {
    this.pos({ osc: 'square', freq: [1800, 1200], dur: 0.07, gain: 0.12, filter: { type: 'bandpass', freq: 2200, q: 3 } }, at, 50);
    this.pos({ osc: 'square', freq: [2000, 1400], dur: 0.07, gain: 0.12, filter: { type: 'bandpass', freq: 2400, q: 3 } }, at, 50);
  }
  swoop(at?: Vec3): void {
    this.pos({ osc: 'noise', freq: 0, dur: 0.5, gain: 0.25, filter: { type: 'bandpass', freq: [400, 2000], q: 1.5 }, attack: 0.15, release: 0.2 }, at, 50);
  }
  thunder(): void {
    this.synth.play({ osc: 'noise', freq: 0, dur: 1.6, gain: 0.3, filter: { type: 'lowpass', freq: [300, 80] }, attack: 0.1, release: 0.8 });
  }
  bury(at?: Vec3): void {
    this.pos({ osc: 'noise', freq: 0, dur: 0.4, gain: 0.35, filter: { type: 'lowpass', freq: [200, 2500] }, attack: 0.1 }, at);
  }
  chaliceGrab(): void {
    const chord = [261.6, 329.6, 392, 523.3, 659.3];
    chord.forEach((f, i) => this.synth.play({ osc: 'triangle', freq: f, dur: 2.4, gain: 0.14, attack: 0.4, release: 1.2, reverb: 0.8 }, i * 0.05));
  }
  win(): void {
    const seq = [523.3, 659.3, 784, 1046.5, 1318.5];
    seq.forEach((f, i) => this.synth.play({ osc: 'triangle', freq: f, dur: 0.6, gain: 0.2, reverb: 0.7, release: 0.5 }, i * 0.15));
    this.synth.play({ osc: 'sine', freq: 130.8, dur: 2.5, gain: 0.25, attack: 0.3, release: 1, reverb: 0.6 }, 0.6);
  }
  uiClick(): void { this.synth.play({ osc: 'triangle', freq: [700, 900], dur: 0.05, gain: 0.15 }); }
  secret(): void {
    [0, 4, 7, 12, 16, 19].forEach((n, i) => this.synth.play({ osc: 'sine', freq: 440 * Math.pow(2, n / 12), dur: 0.5, gain: 0.15, reverb: 0.8 }, i * 0.1));
  }

  // ---- ambient beds per biome (§8.6) ----
  static readonly BEDS: Record<string, AmbientLayer[]> = {
    hearth: [
      { osc: 'noise', freq: 0, filter: { type: 'lowpass', freq: 500, q: 0.5 }, gain: 0.05, lfo: { rate: 0.15, depth: 0.5 } },
      { osc: 'sine', freq: 110, gain: 0.03, lfo: { rate: 0.08, depth: 0.4 } },
      { osc: 'triangle', freq: 1760, filter: { type: 'bandpass', freq: 1800, q: 8 }, gain: 0.006, lfo: { rate: 2.3, depth: 0.9 } },
    ],
    marsh: [
      { osc: 'noise', freq: 0, filter: { type: 'lowpass', freq: 260, q: 0.7 }, gain: 0.08, lfo: { rate: 0.1, depth: 0.5 } },
      { osc: 'sine', freq: 55, gain: 0.05, lfo: { rate: 0.05, depth: 0.5 } },
      { osc: 'square', freq: 640, filter: { type: 'bandpass', freq: 700, q: 12 }, gain: 0.004, lfo: { rate: 3.1, depth: 1 } },
    ],
    dunes: [
      { osc: 'noise', freq: 0, filter: { type: 'bandpass', freq: 900, q: 0.4 }, gain: 0.07, lfo: { rate: 0.2, depth: 0.7 } },
      { osc: 'sine', freq: 220, gain: 0.015, lfo: { rate: 0.3, depth: 0.8 } },
    ],
    ironwood: [
      { osc: 'noise', freq: 0, filter: { type: 'lowpass', freq: 180, q: 0.5 }, gain: 0.06 },
      { osc: 'sine', freq: 82, gain: 0.04, lfo: { rate: 0.07, depth: 0.5 } },
      { osc: 'sine', freq: 1975, filter: { type: 'bandpass', freq: 2000, q: 20 }, gain: 0.004, lfo: { rate: 0.9, depth: 1 } },
    ],
    cistern: [
      { osc: 'sine', freq: 41, gain: 0.07, lfo: { rate: 0.04, depth: 0.4 } },
      { osc: 'noise', freq: 0, filter: { type: 'lowpass', freq: 120, q: 0.8 }, gain: 0.05 },
      { osc: 'sine', freq: 1200, filter: { type: 'bandpass', freq: 1200, q: 30 }, gain: 0.003, lfo: { rate: 0.37, depth: 1 } },
    ],
  };

  setBiomeAmbient(name: string | null, fade = 2): void {
    for (const k of Object.keys(Sfx.BEDS)) {
      this.synth.ambient(k, Sfx.BEDS[k], k === name ? 1 : 0, fade);
    }
  }
  drip(at?: Vec3): void {
    this.pos({ osc: 'sine', freq: [1400 + Math.random() * 600, 700], dur: 0.08, gain: 0.12, reverb: 0.9, release: 0.15 }, at, 30);
  }
}
